import type { ProductListItem } from "@repo/shared/types";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  useProductCorrelationMatrix,
  useSaveProductCorrelations,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { buildCorrelationCsv, downloadCsv, parseCorrelationCsv } from "./correlation-csv";
import { buildCorrelationPayload } from "./correlation-payload";
import { T } from "./product-correlation-matrix.i18n";

/** A correlation cell is valid when empty (null) or a finite number within [-1, 1]. */
const isInRange = (v: number | null): boolean =>
  v === null || (Number.isFinite(v) && v >= -1 && v <= 1);

type CorrelationMatrixData = Record<string, Record<string, number | null>>;

// QP-164 — mirrors correlation-matrix.tsx's asset-class threshold.
const COLLAPSE_THRESHOLD = 10;

const PRODUCT_COLORS = ["bg-chart-1", "bg-chart-2", "bg-chart-3", "bg-chart-4", "bg-chart-5"];

type ProductCorrelationMatrixProps = {
  portfolioCollectionId: string;
  products: ProductListItem[];
};

export function ProductCorrelationMatrix({
  portfolioCollectionId,
  products,
}: ProductCorrelationMatrixProps) {
  const localize = useLocalize();
  const t = useT(T);

  const { correlations: initialCorrelations, isLoading: loading } =
    useProductCorrelationMatrix(portfolioCollectionId);

  const saveCorrelations = useSaveProductCorrelations();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Editable local copy, resynced whenever the query's data reference
  // actually changes (see correlation-matrix.tsx for why a plain
  // one-shot-initialized flag doesn't work here).
  const [storedCorrelations, setStoredCorrelations] = useState<CorrelationMatrixData>({});

  useEffect(() => {
    if (!loading) {
      setStoredCorrelations(initialCorrelations as CorrelationMatrixData);
    }
  }, [initialCorrelations, loading]);

  const handleCorrelationChange = (fromId: string, toId: string, value: string) => {
    const numValue = value === "" ? null : parseFloat(value);

    setStoredCorrelations((prev) => {
      const newMatrix: CorrelationMatrixData = {};
      for (const [fId, toCorrs] of Object.entries(prev)) {
        newMatrix[fId] = { ...toCorrs };
      }
      if (!newMatrix[fromId]) {
        newMatrix[fromId] = {};
      }
      newMatrix[fromId][toId] = numValue;
      return newMatrix;
    });
  };

  const getCorrelation = (
    matrix: CorrelationMatrixData,
    fromId: string,
    toId: string,
  ): number | null => {
    return matrix[fromId]?.[toId] ?? null;
  };

  const handleSave = () => {
    setError(null);
    setSuccess(false);

    const hasInvalid = Object.values(storedCorrelations).some((toMap) =>
      Object.values(toMap).some((v) => !isInRange(v)),
    );
    if (hasInvalid) {
      setError(t.valuesOutOfRange);
      return;
    }

    saveCorrelations.mutate(
      {
        collectionId: portfolioCollectionId,
        data: {
          matrix: buildCorrelationPayload(
            products.map((e) => e.id),
            storedCorrelations,
          ),
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setSuccessMessage(t.correlationsSaved);
          setTimeout(() => setSuccess(false), 3000);
        },
        onError: (err: Error) => {
          setError(err.message || t.failedToSave);
        },
      },
    );
  };

  // QP-164 — CSV export/import, keyed by ISIN. Products without an ISIN
  // have no stable external identity, so they're excluded from the CSV
  // (the manual grid remains the only way to set their correlations).
  const productsWithIsin = products.filter((p) => p.isin);
  const csvEntities = productsWithIsin.map((p) => ({ id: p.id, label: p.isin! }));
  const hasDuplicateIsins = new Set(csvEntities.map((e) => e.label)).size !== csvEntities.length;
  const productsWithoutIsinCount = products.length - productsWithIsin.length;
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadCsv = () => {
    const csv = buildCorrelationCsv(csvEntities, storedCorrelations);
    downloadCsv(csv, "product-correlations.csv");
  };

  const handleUploadCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (csvFileInputRef.current) csvFileInputRef.current.value = "";
    if (!file) return;

    setError(null);
    setSuccess(false);

    const text = await file.text();
    const parsed = parseCorrelationCsv(text, csvEntities);
    if (parsed.error !== null) {
      setError(`${t.failedToParseCsv}: ${parsed.error}`);
      return;
    }

    saveCorrelations.mutate(
      {
        collectionId: portfolioCollectionId,
        data: {
          matrix: buildCorrelationPayload(
            // Only the entities a CSV can carry: a product without an ISIN is
            // not in the file, so its pairs are not the file's to clear.
            csvEntities.map((e) => e.id),
            parsed.matrix,
            { clearMissing: true },
          ),
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
          setSuccessMessage(t.csvUploaded);
          setTimeout(() => setSuccess(false), 3000);
        },
        onError: (err: Error) => {
          setError(err.message || t.failedToSave);
        },
      },
    );
  };

  const saving = saveCorrelations.isPending;

  // QP-164 — above the threshold, the grid is hidden behind a status summary.
  const isCollapsed = products.length > COLLAPSE_THRESHOLD;
  const requiredPairs = (products.length * (products.length - 1)) / 2;
  let definedPairs = 0;
  if (isCollapsed) {
    for (let i = 0; i < products.length; i++) {
      for (let j = i + 1; j < products.length; j++) {
        const aId = products[i]!.id;
        const bId = products[j]!.id;
        const value =
          getCorrelation(storedCorrelations, aId, bId) ??
          getCorrelation(storedCorrelations, bId, aId);
        if (isInRange(value) && value !== null) definedPairs++;
      }
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>{t.loadingCorrelations}</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {success && (
          <div className="text-sm text-success bg-success/10 p-3 rounded-md">{successMessage}</div>
        )}

        {hasDuplicateIsins && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4" />
            {t.duplicateIsinsBlockCsv}
          </div>
        )}

        {/* QP-164 — CSV export/import, available at any product count. */}
        <div className="flex items-center justify-end gap-2">
          <input
            ref={csvFileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleUploadCsvFile}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadCsv}
            disabled={hasDuplicateIsins}
          >
            {t.downloadCsv}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => csvFileInputRef.current?.click()}
            disabled={hasDuplicateIsins || saving}
          >
            {t.uploadCsv}
          </Button>
        </div>

        {productsWithoutIsinCount > 0 && (
          <p className="text-xs text-muted-foreground">
            {t.productsWithoutIsinExcluded(productsWithoutIsinCount)}
          </p>
        )}

        {isCollapsed ? (
          <div className="space-y-2 rounded-md border p-4">
            <p className="text-sm font-medium">{t.collapsedTitle}</p>
            <p className="text-sm text-muted-foreground">
              {t.collapsedReason(products.length, COLLAPSE_THRESHOLD)}
            </p>
            <p className="text-sm font-medium">
              {t.correlationsStatus(definedPairs, requiredPairs)}
            </p>
            <p className="text-xs text-muted-foreground">{t.correlationsStatusHint}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-end">
              <Button size="sm" onClick={handleSave} loading={saving}>
                {t.save}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-2 text-left border-b">{t.product}</th>
                    {products.map((p, idx) => (
                      <th key={p.id} className="p-2 text-center border-b min-w-[100px]">
                        <div className="flex items-center justify-center">
                          <div
                            className={`w-4 h-4 rounded-full ${PRODUCT_COLORS[idx % PRODUCT_COLORS.length]}`}
                            title={localize(p.nameI18n)}
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((rowProduct, rowIdx) => (
                    <tr key={rowProduct.id}>
                      <td className="p-2 border-b">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-3 h-3 rounded-full ${PRODUCT_COLORS[rowIdx % PRODUCT_COLORS.length]}`}
                          />
                          <span className="text-sm font-medium">
                            {localize(rowProduct.nameI18n)}
                          </span>
                        </div>
                      </td>
                      {products.map((colProduct, colIdx) => {
                        const isDiagonal = rowProduct.id === colProduct.id;
                        const isUpperTriangle = rowIdx < colIdx;

                        const storedValue = isUpperTriangle
                          ? getCorrelation(storedCorrelations, colProduct.id, rowProduct.id)
                          : getCorrelation(storedCorrelations, rowProduct.id, colProduct.id);

                        return (
                          <td
                            key={colProduct.id}
                            className={`p-2 border-b text-center ${isDiagonal ? "bg-muted/50" : ""} ${isUpperTriangle ? "bg-muted/30" : ""}`}
                          >
                            {isDiagonal ? (
                              <span className="text-xs text-muted-foreground">1.00</span>
                            ) : isUpperTriangle ? (
                              <span className="text-xs text-muted-foreground">
                                {storedValue !== null ? storedValue.toFixed(2) : "-"}
                              </span>
                            ) : (
                              <Input
                                type="number"
                                step="0.01"
                                min="-1"
                                max="1"
                                className={cn(
                                  "w-20 h-7 text-xs text-center mx-auto",
                                  !isInRange(storedValue) &&
                                    "border-destructive focus-visible:ring-destructive",
                                )}
                                placeholder="0.00"
                                value={storedValue !== null ? storedValue.toString() : ""}
                                onChange={(e) =>
                                  handleCorrelationChange(
                                    rowProduct.id,
                                    colProduct.id,
                                    e.target.value,
                                  )
                                }
                                disabled={saving}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">{t.helpText}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
